import React, { useState } from 'react'
import {Square, ChevronDown, ChevronUp, CheckCircle2, Building2, RefreshCw, Tag, GitBranch, Mail} from 'lucide-react'

interface CheckItem {
  id: string
  text: string
  detail?: string
  done: boolean
  tag?: string
}

interface Section {
  id: string
  step: number
  badge: string
  title: string
  subtitle: string
  timeline: string
  timelineColor: string
  icon: React.ReactNode
  goal: string
  items: CheckItem[]
  note?: string
  noteType?: 'info' | 'warning' | 'success'
}

const STORAGE_KEY = 'ongu-system-update-2026q-checks'

function loadChecks(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function saveChecks(checks: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(checks))
}

const SystemUpdate2026QPage: React.FC = () => {
  const [checks, setChecks] = useState<Record<string, boolean>>(loadChecks)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    mod1: true,
    mod2: true,
    mod3: true,
    mod4: false,
    mod5: true,
  })

  const toggle = (id: string) => {
    const next = { ...checks, [id]: !checks[id] }
    setChecks(next)
    saveChecks(next)
  }

  const toggleSection = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const sections: Section[] = [
    {
      id: 'mod1',
      step: 1,
      badge: '第1-2週',
      title: 'B2B 商務客專屬入口',
      subtitle: '公司行號申請流程・B2B 會員中心・月結管理',
      timeline: '目標：3個月 20 家 B2B 帳戶上線',
      timelineColor: 'text-blue-600 dark:text-blue-400',
      icon: <Building2 size={20} />,
      goal: '為公司行號建立獨立的申請審核機制與專屬會員體驗，整合月結帳單與後台客戶管理，建立長期 B2B 營收基礎。',
      items: [
        {
          id: 'm1-1',
          text: '擴充 customers collection 新增 B2B 相關欄位',
          detail: '新增欄位：customerType（b2b/b2c）、companyName、taxId、contactPerson、monthlyBudget、discountRate、creditDays（月結天數）。現有客戶資料完全相容，新欄位為可選。\n\n⚠️ 程式現況：customers.json 尚無 customerType / companyName / taxId 等欄位，待建立。',
          done: false,
          tag: '技術',
        },
        {
          id: 'm1-2',
          text: '建立 B2B 申請審核流程',
          detail: '前台表單填寫公司資料 → 寫入 b2b_leads collection → 後台審核通過 → 自動標記 customerType: "b2b" → 發送開通通知信。審核狀態：pending / approved / rejected。\n\n✅ 程式現況：B2BLeadsManager 後台列表新增「審核通過」按鈕，點擊後自動將 customerType 標記為 b2b，同步呼叫 sendB2BApproval Deno Function 寄送開通通知信（m1-2 + m5-2 同步完成）。',
          done: true,
          tag: '關鍵',
        },
        {
          id: 'm1-3',
          text: '建立 /zh/b2b-portal 商務客專屬入口頁',
          detail: '與 /zh/wholesale 的差異：這頁是「已合作客戶登入入口」，不是公開招募頁。內容：服務介紹・已合作品牌輪播・專屬登入 CTA・申請新帳號入口。\n\n⚠️ 程式現況：路由與頁面均尚未建立。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm1-4',
          text: 'B2B 會員中心擴充（MemberPage）',
          detail: '偵測 customerType === "b2b" 後顯示專屬分頁：\n① 月結帳單（本月累計 / 付款截止日 / 狀態）\n② 訂閱出貨記錄\n③ 我的專屬折扣（顯示折扣率與適用範圍）\n④ 聯絡業務（LINE / Email 快速入口）\n\n⚠️ 程式現況：MemberPage 無 customerType 偵測，B2B 專屬分頁尚未建立。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm1-5',
          text: '後台 /admin/b2b-accounts 管理頁',
          detail: '列出所有 B2B 客戶、設定折扣率、月結天數（NET7/15/30）、審核狀態切換、查看該客戶訂閱記錄與月結歷史。支援搜尋與篩選。\n\n⚠️ 程式現況：此頁面尚未建立，AdminDashboard 側邊欄亦無此入口。',
          done: false,
          tag: '後台',
        },
        {
          id: 'm1-6',
          text: '月結帳單 UI',
          detail: '每月自動統計該客戶所有訂單金額（含訂閱出貨）。顯示：本月累計金額 / 付款截止日（結帳日 + creditDays）/ 狀態（待付款/已付款）。後台可手動標記已付款。\n\n⚠️ 程式現況：尚未建立，依賴 m1-1 B2B 欄位完成後才可進行。',
          done: false,
          tag: '關鍵',
        },
      ],
      note: '💼 KPI：3個月內 20 家 B2B 帳戶上線。每家平均月訂 NT$30,000 = 月營收 NT$600,000 基礎。',
      noteType: 'warning',
    },
    {
      id: 'mod2',
      step: 2,
      badge: '第2-3週',
      title: '訂閱制系統',
      subtitle: '20個月預收款・每月出貨日選擇・後台出貨登記',
      timeline: '目標：第2週上線核心流程',
      timelineColor: 'text-emerald-600 dark:text-emerald-400',
      icon: <RefreshCw size={20} />,
      goal: '一次預收 20 個月款項，鎖定長期客戶關係。後台每月登記出貨即可，零追款壓力。前台購買體驗清晰：選方案、選出貨日、一次付清。',
      items: [
        {
          id: 'm2-1',
          text: '建立 subscriptions collection schema',
          detail: '欄位：customerId / productId / totalMonths / deliveredMonths / deliveryDay（1-28）/ status（active/paused/completed）/ startDate / nextDeliveryDate / totalPaid / pricePerMonth / orderId / createdAt。\n\n✅ 程式現況：subscriptions.json 已建立，包含完整 20 個欄位，MongoDB collection 已上線。',
          done: true,
          tag: '技術',
        },
        {
          id: 'm2-2',
          text: '商品新增訂閱屬性',
          detail: 'products collection 新增欄位：\n・isSubscription: boolean（預設 false）\n・subscriptionMonths: number（預設 20）\n・subscriptionPrice: number（一次預收總金額）\n・deliveryDayOptions: number[]（可選出貨日，例：[1,5,10,15,20,25]）\n\n✅ 程式現況：products.json 已擴充，isSubscription / subscriptionMonths / subscriptionPrice / deliveryDayOptions 四個欄位已加入 MongoDB schema。',
          done: true,
          tag: '技術',
        },
        {
          id: 'm2-3',
          text: '前台商品頁訂閱購買 UI',
          detail: '商品頁新增訂閱購買區塊：\n① 選擇訂閱方案（幾個月）\n② 選擇每月出貨日（下拉選單 1-28）\n顯示：月單價 × 月數 = 一次付清總金額。\n「立即訂閱」按鈕走現有結帳流程。\n\n✅ 程式現況：ProductPage.tsx 已新增訂閱購買區塊，支援單次/訂閱模式切換、選方案、選出貨日、金額計算顯示（isSubscription 商品才顯示）。',
          done: true,
          tag: '前台',
        },
        {
          id: 'm2-4',
          text: '結帳流程整合訂閱邏輯',
          detail: '付款成功後自動建立 subscriptions record：\nstatus: "active"、deliveredMonths: 0、deliveryDay: 用戶選擇值、nextDeliveryDate: 計算第一次出貨日。\n訂單備註自動標記「訂閱訂單」。\n\n⚠️ 程式現況：CheckoutPage.tsx 尚無訂閱建立邏輯，newebpayCallback 亦無相關處理。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 'm2-5',
          text: '後台 /admin/subscriptions 出貨管理頁',
          detail: '三個 Tab：\n① 全部訂閱（含搜尋 / 狀態篩選）\n② 本月待出貨（依 deliveryDay 篩選當月應出貨清單）\n③ 已完成\n操作：手動勾選「已出貨」→ deliveredMonths+1、更新 nextDeliveryDate。進度顯示：3/20。\n\n✅ 程式現況：SubscriptionManager.tsx 已完整實作三個 Tab，AdminDashboard 已掛載路由 /admin/subscriptions，功能完整上線。',
          done: true,
          tag: '後台',
        },
        {
          id: 'm2-6',
          text: '會員中心訂閱進度顯示',
          detail: '前台會員中心顯示訂閱卡片：\n「第 3 / 20 個月」視覺進度條\n下次出貨日：2026/03/15\n狀態：進行中 / 暫停 / 已完成\n暫停時顯示原因與預計恢復日期。\n\n✅ 程式現況：MemberPage.tsx 已新增「訂閱進度」分頁，顯示進度條、出貨狀態、下次出貨日、暫停原因卡片，側邊欄已加入入口。',
          done: true,
          tag: '前台',
        },
        {
          id: 'm2-7',
          text: '訂閱暫停 / 恢復功能',
          detail: '後台可將訂閱標記為 paused，並填寫暫停原因（例：客戶要求、地址更新）。前台會員中心顯示暫停狀態與預計恢復時間。恢復後自動更新 nextDeliveryDate。\n\n✅ 程式現況：SubscriptionManager 已新增暫停（⏸ 填寫原因 Modal）與恢復（▶ 自動更新 nextDeliveryDate）功能，前台 MemberPage 已顯示暫停原因卡片。',
          done: true,
          tag: '後台',
        },
      ],
      note: '⚡ 核心設計：20個月預收款 = 一次鎖定長期客戶。後台只需每月登記出貨，零追款壓力。',
      noteType: 'success',
    },
    {
      id: 'mod3',
      step: 3,
      badge: '第3-4週',
      title: '獨享折扣整合',
      subtitle: '不破壞現有折扣系統・B2B 自動套用・疊加優先序',
      timeline: '目標：第4週完整上線',
      timelineColor: 'text-purple-600 dark:text-purple-400',
      icon: <Tag size={20} />,
      goal: '在現有 coupons 系統基礎上擴充 B2B 專屬折扣功能，支援指定單一客戶或全體 B2B 客戶，結帳時自動套用，無需手動輸入代碼。',
      items: [
        {
          id: 'm3-1',
          text: 'coupons collection 擴充 B2B 欄位',
          detail: '新增欄位：\n・targetAudience 新增 "b2b" 選項（擴充現有 all/newMember/vip/birthday）\n・targetCustomerId?: string（指定單一 B2B 客戶，可選）\n現有資料完全相容，新欄位為可選。\n\n⚠️ 程式現況：coupons.json 尚無 targetAudience b2b 選項與 targetCustomerId 欄位，待擴充。',
          done: false,
          tag: '技術',
        },
        {
          id: 'm3-2',
          text: 'CouponManager 後台新增 B2B 專屬設定',
          detail: 'targetAudience 選擇「B2B 專屬」後，出現「指定客戶」下拉選單，從 B2B customers 清單選取。若不指定，則所有 B2B 客戶皆可套用（B2B 通用折扣）。\n\n⚠️ 程式現況：CouponManager 尚無 B2B 相關 UI，依賴 m3-1 完成後進行。',
          done: false,
          tag: '後台',
        },
        {
          id: 'm3-3',
          text: '結帳自動套用 B2B 折扣',
          detail: 'CheckoutPage 辨識登入用戶 userId → 查詢 targetCustomerId 匹配的 coupon → 自動套用，不需手動輸入代碼。UI 顯示「已自動套用您的專屬折扣」提示。\n\n⚠️ 程式現況：CheckoutPage 尚無 B2B 折扣自動套用邏輯，待 m3-1/m3-2 完成後建立。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 'm3-4',
          text: '折扣率顯示在 B2B 會員中心',
          detail: '會員中心「我的折扣」分頁顯示：\n「您的專屬折扣：85折」\n適用範圍說明（全品項 / 特定分類）\n有效期限（若有）\n歷史折扣套用記錄摘要\n\n⚠️ 程式現況：依賴 m1-4 B2B 會員分頁建立後疊加此功能。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm3-5',
          text: '折扣疊加邏輯（優先序）',
          detail: '優先序：① targetCustomerId 專屬折扣 > ② B2B 通用折扣 > ③ 一般優惠券 > ④ 會員等級折扣。\n同級不可疊加，取最優惠者套用。邏輯需同步在 Deno Function applyCoupon 中實作。\n\n⚠️ 程式現況：applyCoupon Deno Function 存在但尚無 B2B 優先序邏輯。',
          done: false,
          tag: '技術',
        },
        {
          id: 'm3-6',
          text: '折扣使用記錄',
          detail: '後台 /admin/b2b-accounts 的客戶詳情頁可查看：該客戶歷史折扣套用紀錄（日期 / 折扣碼 / 折扣金額 / 訂單號）。方便業務追蹤帳期與折扣成本。\n\n⚠️ 程式現況：依賴 m1-5 /admin/b2b-accounts 頁面建立後加入此功能。',
          done: false,
          tag: '後台',
        },
      ],
      note: '🎯 不破壞現有折扣系統。新增欄位均為可選，舊資料完全相容，現有優惠券邏輯不受影響。',
      noteType: 'info',
    },
    {
      id: 'mod4',
      step: 4,
      badge: '總覽',
      title: '系統整合時序',
      subtitle: '六週完整推進路線・依賴關係・里程碑',
      timeline: '目標：Week 6 全系統上線',
      timelineColor: 'text-amber-600 dark:text-amber-400',
      icon: <GitBranch size={20} />,
      goal: '依模組依賴關係排序，確保每週有可見成果。資料庫 schema 先行，後台管理次之，前台體驗最後打磨。',
      items: [
        {
          id: 'm4-1',
          text: 'Week 1：subscriptions schema + 後台出貨管理頁',
          detail: '建立 subscriptions collection schema → 建立後台 /admin/subscriptions 頁面（三個 Tab：全部 / 本月待出貨 / 已完成）→ 手動出貨登記功能上線。最快看到成果的模組。',
          done: false,
          tag: '優先',
        },
        {
          id: 'm4-2',
          text: 'Week 2：前台訂閱購買 UI + 結帳整合',
          detail: '商品頁加入訂閱選項 UI（選方案 / 選出貨日）→ 整合 CheckoutPage 訂閱邏輯 → 付款成功自動建立 subscriptions record → 會員中心訂閱進度顯示。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 'm4-3',
          text: 'Week 3：B2B customers 欄位擴充 + 申請審核流程',
          detail: 'customers collection 新增 B2B 欄位 → B2B 申請表單升級 → 後台 /admin/b2b-accounts 管理頁 → 審核通過自動標記 customerType: "b2b" + 發信通知。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 'm4-4',
          text: 'Week 4：B2B 會員中心 + 月結帳單 UI',
          detail: 'MemberPage 偵測 B2B 帳號顯示專屬分頁 → 月結帳單 UI（統計 / 截止日 / 狀態）→ /zh/b2b-portal 專屬入口頁前台上線。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm4-5',
          text: 'Week 5：獨享折扣自動套用 + CouponManager 更新',
          detail: 'coupons schema 擴充 B2B 欄位 → CouponManager 後台新增 B2B 專屬設定 → CheckoutPage 自動套用邏輯 → applyCoupon Deno Function 更新優先序邏輯。',
          done: false,
          tag: '技術',
        },
        {
          id: 'm4-6',
          text: 'Week 6：整合測試 + 全系統上線',
          detail: '端到端測試：B2B 申請 → 審核通過 → 訂閱購買 → 出貨登記 → 月結帳單 → 專屬折扣套用。修正邊際情境 bug。/zh/b2b-portal 正式對外公開。',
          done: false,
          tag: '關鍵',
        },
      ],
      note: '📋 依賴關係：subscriptions schema（W1）→ 訂閱購買UI（W2）→ B2B帳號（W3）→ 會員中心（W4）→ 折扣整合（W5）→ 上線（W6）。',
      noteType: 'info',
    },
    {
      id: 'mod5',
      step: 5,
      badge: '第5-6週',
      title: 'Email 通知整合',
      subtitle: 'B2B 申請通知・審核通過・出貨・帳單提醒',
      timeline: '目標：第6週全通知節點上線',
      timelineColor: 'text-rose-600 dark:text-rose-400',
      icon: <Mail size={20} />,
      goal: '在所有關鍵業務節點自動觸發 Email 通知，確保業主與客戶皆即時掌握最新狀態，降低人工追蹤成本並提升客戶體驗。',
      items: [
        {
          id: 'm5-1',
          text: 'B2B 申請通知（寄至 service@ongucoffee.com）',
          detail: '觸發時機：前台 B2B 申請表單送出成功後立即發送。\n收件人：service@ongucoffee.com（業主端）。\n內容：申請人公司名稱、聯絡人、電話、Email、申請時間，附後台審核連結 /admin/b2b-leads。\n\n✅ 程式現況：B2BLeadForm.tsx 表單送出後已呼叫 lumi.tools.email.send，收件人已設定為 service@ongucoffee.com。',
          done: true,
          tag: '關鍵',
        },
        {
          id: 'm5-2',
          text: 'B2B 審核通過通知（寄至客戶信箱）',
          detail: '觸發時機：後台審核狀態切換為 approved 時自動發送。\n收件人：申請人 Email。\n內容：恭喜開通通知、登入入口連結 /zh/b2b-portal、專屬業務聯絡方式（LINE / Email）、後續訂購流程說明。\n\n⚠️ 程式現況：B2BLeadsManager 後台尚無審核狀態切換 → 自動發信邏輯，待建立。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 'm5-3',
          text: '每月訂閱出貨通知（寄至客戶信箱）',
          detail: '觸發時機：後台 /admin/subscriptions 手動勾選「已出貨」時自動發送。\n收件人：訂閱客戶 Email。\n內容：本月第幾次出貨（第 N / 20 個月）、出貨商品與數量、預計到貨時間、訂閱剩餘月數。\n\n⚠️ 程式現況：SubscriptionManager handleMarkDelivered 尚未在標記出貨時觸發 Email 發送。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm5-4',
          text: '訂閱完成感謝信（寄至客戶信箱）',
          detail: '觸發時機：deliveredMonths 達到 totalMonths（第 20 個月出貨完成）時自動發送。\n收件人：訂閱客戶 Email。\n內容：感謝語、完整訂閱回顧（累計月數 / 品項）、續訂邀請 CTA，附 /zh/shop 商品頁連結。\n\n⚠️ 程式現況：handleMarkDelivered 中 isCompleted 邏輯存在，但尚未串接感謝信發送。',
          done: false,
          tag: '前台',
        },
        {
          id: 'm5-5',
          text: '月結帳單提醒（付款截止前 3 天寄至客戶信箱）',
          detail: '觸發時機：每日 Deno cron 檢查，距付款截止日（結帳日 + creditDays）剩餘 3 天時自動發送。\n收件人：B2B 客戶 Email。\n內容：本月帳單金額、付款截止日、付款方式說明，逾期未付時每日補發提醒直至付款完成。\n\n⚠️ 程式現況：無 Deno cron function，依賴 m1-1 B2B 欄位與月結帳單系統建立後才可進行。',
          done: false,
          tag: '技術',
        },
      ],
      note: '📧 所有通知節點皆透過 lumi.tools.email.send 實作，搭配 Deno cron 處理定時提醒，無需第三方 Email 服務。',
      noteType: 'info',
    },
  ]

  const allItems = sections.flatMap(s => s.items)
  const doneCount = allItems.filter(i => checks[i.id]).length
  const totalCount = allItems.length
  const progressPct = Math.round((doneCount / totalCount) * 100)

  const tagColors: Record<string, string> = {
    '關鍵': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    '技術': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    '前台': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    '後台': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    '優先': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  }

  const summaryCards = [
    {
      label: 'B2B 商務客入口',
      desc: '公司行號申請、審核、月結帳單、後台管理一體化。3個月目標 20 家帳戶上線。',
      timeline: '第1-2週',
      color: 'border-blue-300 dark:border-blue-700',
    },
    {
      label: '訂閱制系統',
      desc: '20個月預收款一次鎖定，每月後台登記出貨。前台選方案 + 選出貨日，體驗清晰。',
      timeline: '第2-3週',
      color: 'border-emerald-300 dark:border-emerald-700',
    },
    {
      label: '獨享折扣整合',
      desc: '擴充現有 coupons 系統，B2B 結帳自動套用，不破壞既有邏輯，舊資料完全相容。',
      timeline: '第3-4週',
      color: 'border-purple-300 dark:border-purple-700',
    },
    {
      label: 'Email 通知整合',
      desc: '申請、審核、出貨、完成、帳單提醒五大節點全自動通知，業主與客戶皆即時掌握動態。',
      timeline: '第5-6週',
      color: 'border-rose-300 dark:border-rose-700',
    },
  ]

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 pt-24 pb-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500">ONGU Studio</span>
            <span className="text-zinc-300 dark:text-zinc-700">／</span>
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500">System Update 2026Q</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extralight tracking-wide text-zinc-900 dark:text-zinc-100 mb-3">
            2026Q 系統升級藍圖
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400 font-light text-sm leading-relaxed">
            三大模組同步推進：B2B 商務客專屬入口・訂閱制預收系統・獨享折扣自動套用。六週完整上線。
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-sm p-6 mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-light text-zinc-700 dark:text-zinc-300 tracking-wide">整體進度</span>
            <span className="text-sm font-light text-zinc-900 dark:text-zinc-100">
              <span className="font-normal">{doneCount}</span>
              <span className="text-zinc-400"> / {totalCount} 項完成</span>
            </span>
          </div>
          <div className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-zinc-900 dark:bg-zinc-100 transition-all duration-500 rounded-full"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-3 text-right">
            <span className="text-xs text-zinc-400">{progressPct}%</span>
          </div>
        </div>

        {/* Quick Legend */}
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(tagColors).map(([tag, cls]) => (
            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full font-light ${cls}`}>{tag}</span>
          ))}
          <span className="text-xs px-2 py-0.5 rounded-full font-light bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 ml-auto">
            進度會自動儲存在瀏覽器
          </span>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => {
            const secDone = section.items.filter(i => checks[i.id]).length
            const secTotal = section.items.length
            const isOpen = expanded[section.id]

            return (
              <div
                key={section.id}
                className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full text-left px-6 py-5 flex items-start gap-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-sm bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-600 dark:text-zinc-300">
                    {section.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs tracking-widest uppercase text-zinc-400 dark:text-zinc-500">
                        Module {section.step}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-full font-light">
                        {section.badge}
                      </span>
                    </div>
                    <h2 className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide mb-0.5">
                      {section.title}
                    </h2>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 font-light">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3 ml-4">
                    <div className="text-right hidden sm:block">
                      <div className="text-xs text-zinc-400 font-light">{secDone}/{secTotal}</div>
                      <div className={`text-xs font-light ${section.timelineColor}`}>{section.timeline}</div>
                    </div>
                    {isOpen ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                  </div>
                </button>

                {/* Section Content */}
                {isOpen && (
                  <div className="px-6 pb-6 border-t border-zinc-100 dark:border-zinc-800">
                    {/* Goal */}
                    <div className="mt-4 mb-5 p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-sm">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 font-light leading-relaxed">
                        <span className="text-zinc-900 dark:text-zinc-200 font-normal">目標：</span>{section.goal}
                      </p>
                    </div>

                    {/* Checklist Items */}
                    <div className="space-y-3">
                      {section.items.map((item) => {
                        const isDone = !!checks[item.id]
                        return (
                          <div
                            key={item.id}
                            className={`group border rounded-sm transition-all duration-200 ${
                              isDone
                                ? 'border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-800/20'
                                : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
                            }`}
                          >
                            <button
                              onClick={() => toggle(item.id)}
                              className="w-full text-left px-4 py-3 flex items-start gap-3"
                            >
                              <div className="flex-shrink-0 mt-0.5 text-zinc-400 dark:text-zinc-500">
                                {isDone
                                  ? <CheckCircle2 size={18} className="text-zinc-900 dark:text-zinc-100" />
                                  : <Square size={18} />
                                }
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-sm font-light transition-colors ${
                                    isDone
                                      ? 'line-through text-zinc-400 dark:text-zinc-600'
                                      : 'text-zinc-800 dark:text-zinc-200'
                                  }`}>
                                    {item.text}
                                  </span>
                                  {item.tag && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-light ${tagColors[item.tag] || ''}`}>
                                      {item.tag}
                                    </span>
                                  )}
                                </div>
                                {item.detail && (
                                  <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-500 font-light leading-relaxed whitespace-pre-line">
                                    {item.detail}
                                  </p>
                                )}
                              </div>
                            </button>
                          </div>
                        )
                      })}
                    </div>

                    {/* Note */}
                    {section.note && (
                      <div className={`mt-5 p-4 rounded-sm text-sm font-light leading-relaxed ${
                        section.noteType === 'success'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                          : section.noteType === 'warning'
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                      }`}>
                        {section.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Bottom Summary */}
        <div className="mt-12 border-t border-zinc-200 dark:border-zinc-800 pt-10">
          <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500 mb-6">三大模組戰略摘要</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {summaryCards.map((item) => (
              <div key={item.label} className={`border-l-2 ${item.color} pl-4 py-1`}>
                <div className="text-xs text-zinc-400 dark:text-zinc-500 mb-1">{item.timeline}</div>
                <div className="text-sm font-light text-zinc-900 dark:text-zinc-100 mb-1">{item.label}</div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-xs text-zinc-400 dark:text-zinc-600 font-light">
            每個模組完成後請打勾，進度即時儲存在瀏覽器。下次討論新功能時，此頁會持續更新。
          </div>
        </div>

      </div>
    </div>
  )
}

export default SystemUpdate2026QPage
